<template>
  <li :class="['flipping-card', statusData.cardType]"
       :style="[{ height: cardHeight + 'px' }, { width: cardWidth + 'px' }]">
    <div class="flipping-card__inner">
    <vue-flip v-model="flipped" height="100%" width="100%">
      <template v-slot:front>
        <div
            :aria-hidden="flipped"
            class="flipping-card__front"
            :style="`background-image: url('${card.imageUrl + '?wid=1600&hei=900&fmt=jpeg&op_usm=1,1,5,0&resMode=sharp2&fit=crop&qlt=75,0'}')`"
        >
          <div class="heading" v-html="card.propertyName" />
          <div class="badge" v-html="badgeFrontText"></div>
          <div class="message" v-html="meetingMessageText" />
          <button
            class="button--primary white size-md"
            @click="flipIt"
            :tabindex="flipped ? '-1' : '0'"
            v-text="tc('reservations.actionLabel')"
          />
        </div>
      </template>
      <template v-slot:back>
        <div :aria-hidden="!flipped" class="flipping-card__back">
          <button
              @click="flipIt"
              :tabindex="flipped ? '0' : '-1'"
              :aria-label="tc('reservations.closeButtonLabel')"
              class="flipping-card-close"
          >
            <span class="icon icon-close-v2" aria-hidden="true"></span>
          </button>
          <vue-custom-scrollbar class="scroll-area"
                                :style="[{ height: cardHeight + 'px' }]"
                                :settings="scrollSettings">
            <div class="flipping-card__scroll">
              <div class="dashboard-table dashboard-table--card">
              <div class="badge" v-html="badgeBackText"></div>
              <div class="confirmation full-span-column cell--border-bottom cell--flex">
                <div class="confirmation-wrapper">
                  <img class="logo" :src="card.propertyLogoUrl + '?fmt=png-alpha'" :alt="card.propertyLogoAlt" />
                  <div>
                    <div class="dash-label" v-text="tc('reservations.confirmationLabel')"></div>
                    <div class="dash-content" v-text="card.number"></div>
                  </div>
                </div>
              </div>
              <div class="date-block">
                <div class="date-in">
                  <div class="dash-label" v-text="tc('reservations.checkinLabel')"></div>
                  <div class="dash-content">
                    <div>{{ card.startDate | dayFilter }}</div>
                    <div>{{ card.startDate | dateFilter(lang) }}</div>
                    <sub>{{ tc('reservations.after') }} {{ card.checkInTime  | timeFilter }}</sub>
                  </div>
                </div>
                <span class="icon icon-arrow-right"></span>
                <div class="date-out">
                  <div class="dash-label" v-text="tc('reservations.checkoutLabel')"></div>
                  <div class="dash-content">
                    <div>{{ card.endDate | dayFilter }}</div>
                    <span>{{ card.endDate | dateFilter(lang) }}</span>
                    <sub>{{ tc('reservations.before') }} {{ card.checkOutTime | timeFilter}}</sub>
                  </div>
                </div>
              </div>
              <div class="guests full-span-column cell--border-bottom cell--border-top">
                <div class="dash-label" v-text="tc('reservations.guestroomLabel')"></div>
                <ul class="dash-content comma-lists">
                  <li v-html="guests"></li>
                </ul>
              </div>
              <div v-if="isEnhancements" class="enhancements full-span-column cell--border-bottom">
                <div class="dash-label" v-text="tc('reservations.complimentaryLabel')"></div>
                <ul class="dash-content comma-lists">
                  <li
                      v-for="(enhancements, i) in card.enhancements"
                      :key="i + '-enhancement'"
                      v-html="enhancements"
                  ></li>
                </ul>
              </div>
              <div class="full-span-column control">
                <a
                    v-if="isUpcoming && !card.hideEditPreferenceForUpcoming"
                    :tabindex="flipped ? '0' : '-1'"
                    :href="editPrefUrl"
                    class="button--secondary black size-md outlined"
                    v-text="tc('reservations.editPreferences')"
                ></a>
                <a
                    v-if="isUpcoming && card.hideEditPreferenceForUpcoming"
                    :href="contactUsUrl"
                    :tabindex="flipped ? '0' : '-1'"
                    class="button--primary white size-md"
                    v-text="tc('reservations.contactUsLabel')"
                ></a>
                <a
                    v-if="!card.hideModify && isUpcoming"
                    :tabindex="flipped ? '0' : '-1'"
                    @click.prevent="redirectUser(card)"
                    class="button--primary black size-md"
                    v-text="tc('reservations.modifyLabel')"
                ></a>
                <a
                  v-if="isCancelled"
                  :href="bookButtonUrl"
                  :tabindex="flipped ? '0' : '-1'"
                  class="button--primary black size-md"
                  target="book"
                  v-text="tc('reservations.bookButton')"
                ></a>
                <a
                  v-if="isPast"
                  :href="bookButtonUrl"
                  :tabindex="flipped ? '0' : '-1'"
                  class="button--primary black size-md"
                  target="book"
                  v-text="tc('reservations.bookAgainButton')"
                ></a>
              </div>
            </div>
          </div>
          </vue-custom-scrollbar>
        </div>
      </template>
    </vue-flip>
    </div>
  </li>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import moment from 'moment'
  import VueFlip from 'vue-flip'
  import { formatDate, formatDateZone } from '@utils/date-helpers'
  import vueCustomScrollbar from 'vue-custom-scrollbar'

  export default {
    name: 'flipping-card',
    components: {
      VueFlip,
      vueCustomScrollbar
    },
    props: {
      card: {
        type: Object,
        required: true
      },
      statusData: {
        type: Object,
        required: true
      },
      modifyPage: {
        type: String,
        required: false
      },
    },
    data() {
      return {
        flipped: false,
        cardHeight: 'auto',
        cardWidth: 'auto',
        ratio: 1.3,
        scrollSettings: {
          suppressScrollX: true,
          wheelPropagation: true,
          swipeEasing: true,
        },
      }
    },
    computed: {
      ...mapState('responsive', { wWidth: 'width' }),
      ...mapState('global', ['lang']),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('booking', ['getSummaryDetails', 'getDateRangeInDays', 'monthFormat']),

      countdownDays() {
        return Math.ceil(
          this.getDateRangeInDays({
            stayDateStart: new Date(Date.now()),
            stayDateEnd: this.card.startDate
          })
        )
      },
      monthInDate() {
        return moment(new Date(this.card.startDate), this.monthFormat)
      },
      monthIndex() {
        return this.monthInDate.month() + 1
      },
      month() {
        return this.tc(`calendar.months.${this.monthIndex}`)
      },
      isCancelled() {
        return this.statusData.cardType === 'cancelled'
      },
      isEnhancements() {
        return this.card.enhancements && this.card.enhancements.length > 0 && this.card.enhancements.some((e) => e.trim() !== '')
      },
      isPast() {
        return this.statusData.cardType === 'past'
      },
      isUpcoming() {
        return this.statusData.cardType === 'upcoming'
      },
      guests() {
        return this.getSummaryDetails({
          customQuery: {
            adultGuestCount: this.card.adultsCount,
            childGuestCount: this.card.childCount
          }
        })
      },
      meetingMessageText() {
        const meetingMessage = this.tc('reservations.meetingMessage')
        const startDateFormattedFull = formatDateZone(this.card.startDate, null, this.lang === 'en' ? 'MMM. Do, YYYY' : 'MMM Do, YYYY')
        const startDateFormattedShort = formatDateZone(this.card.startDate, null, this.lang === 'en' ? 'MMM. D, YYYY' : 'MMM D, YYYY')
        const endDateFormattedShort = formatDateZone(this.card.endDate, null, this.lang === 'en' ? 'MMM. D, YYYY' : 'MMM D, YYYY')

        if (this.isUpcoming) {
          return `${meetingMessage} ${startDateFormattedFull}`
        }
        return `${startDateFormattedShort} — ${endDateFormattedShort}`
      },
      badgeFrontText() {
        const { cardType, label } = this.statusData
        if (cardType === 'upcoming') {
          return `${this.tc('reservations.upcomingFrontLabel')} ${formatDateZone(this.card.startDate, null, 'MMM, Do')}`
        }
        if (cardType === 'past') {
          return 'Until Next Time'
        }
        return label
      },
      badgeBackText() {
        const { cardType, label } = this.statusData
        if (cardType === 'past') {
          return 'Until Next Time'
        }
        return label
      },
      editPrefUrl() {
        return `/${this.lang}${this.card.editPreferenceUrl}`
      },
      contactUsUrl() {
        return this.card.contactUsPageUrl || `${this.card.propertyPageUrl}/contact-us`
      },
      bookButtonUrl() {
        return `/?hotelCode=${this.card.propertyId}&openBooking=true`
      },
      isMobile() {
        return this.wWidth <= 1024
      },
    },
    watch: {
      wWidth(val) {
        if (this.isMobile) this.ratio = 1.4
        this.calculateCardDimensions(val)
      },
      card: {
        handler() {
          this.flipped = false
        }
      },
    },
    mounted() {
      this.calculateCardDimensions(this.wWidth)
    },
    filters: {
      dateFilter(date, lang) {
        return formatDateZone(date, null, lang === 'en' ? 'MMM. D[,] YYYY' : 'MMM D[,] YYYY')
      },
      dayFilter(date) {
        return formatDateZone(date, null, 'dddd')
      },
      timeFilter(time) {
        return time ? formatDate(time, 'HH:mm', 'h:mm A') : ''
      }
    },
    methods: {
      flipIt() {
        this.flipped = !this.flipped
      },
      formatDate(date) {
        return new Date(date).toLocaleDateString()
      },
      calculateCardDimensions(wWidth) {
        let cardsPerRow = 3
        if (wWidth <= 1023) {
          cardsPerRow = 2
          if (wWidth <= 768) cardsPerRow = 1
        }
        const cardWrapperWidth = this.$parent.$el.clientWidth
        const cardWidth = (cardWrapperWidth + 40) / cardsPerRow
        this.cardHeight = cardWidth * this.ratio
        this.cardWidth = cardWidth
      },
      async redirectUser(card) {
        let redirectUrl
        if (this.modifyPage !== '' && this.modifyPage) {
          setTimeout(() => {
            redirectUrl = this.modifyPage + card.modifyCancelUrl;
            window.location.href = redirectUrl;
          }, 500);
        } else {
          setTimeout(() => {
            redirectUrl = card.modifyCancelIruUrl
            window.location.href = redirectUrl
          }, 500);
        }
      }
    }
  }
</script>
